import { useCallback, useEffect, useState } from 'react';
import {
  fetchChangeAllOrderFields,
  type ChangeAllOrderFields,
} from '../services/changeAllFields.service';
import {
  CHANGE_ALL_ELIGIBLE_FIELDS,
  REGISTRY_EXEMPT_FIELDS,
} from '../pages/pro/changeAll/eligibleFields';

/** Which order fields Change All currently offers, by camelCase view key. */
export interface ChangeAllFields {
  /** The registry has answered (as opposed to the built-in fallback being in use). */
  ready: boolean;
  /** May this field be given an "apply to all pros" checkbox? */
  isFannable: (viewKey: string) => boolean;
  /**
   * May the banner's "flag all edited" sweep tick this field?
   *
   * A STRICTER question than isFannable, and the difference is the whole reason both
   * exist. Bulk delete draws a checkbox that no flag governs, but a sweep that ticked
   * it would arm a destructive delete across the target set from a single click on a
   * button labelled "flag all edited" — which is exactly what the reason modal and the
   * dedicated checkbox are there to prevent. Drawable and sweepable are not the same
   * permission.
   */
  isSweepable: (viewKey: string) => boolean;
}

// Resolved once per page load and held at module scope so a second mount renders the
// right answer on its FIRST paint. Without this every newly-mounted checkbox would
// start from the fallback set and correct itself a tick later, which on a field an
// admin has just CLOSED means a checkbox that appears and then vanishes.
let resolved: ReadonlySet<string> | null = null;

const keysFrom = (fields: ChangeAllOrderFields): ReadonlySet<string> =>
  new Set(Object.entries(fields).filter(([, f]) => f.changeAll).map(([key]) => key));

/**
 * The Change All field set, read from `field_definitions` (view_key + is_change_all)
 * rather than from code, so an admin can open or close a field in Data Management
 * without a release.
 *
 * FALLS BACK TO THE BUILT-IN SET, never to empty. `CHANGE_ALL_ELIGIBLE_FIELDS` holds
 * exactly what migration 467 seeds, so until the registry answers — the first render,
 * a database without the migration, a failed request — the user sees the same
 * checkboxes they saw before any of this existed. The alternative, an empty set, would
 * silently strip every checkbox off the form on a transient error, and the user's only
 * clue would be that Change All quietly did nothing.
 *
 * Only two components need this (BulkFieldCheckbox, which every checkbox funnels
 * through, and the banner's "flag all edited" sweep), so it is a hook rather than a
 * fourth context the /pro page, the dispatch drawer and every test tree must provide.
 */
export function useChangeAllFields(active: boolean = true): ChangeAllFields {
  const [keys, setKeys] = useState<ReadonlySet<string> | null>(resolved);

  useEffect(() => {
    // Only ask once the registry can actually be USED. Both callers live on /pro, which
    // mounts for every user on every pro, so an unconditional fetch asked
    // GET /orders/change-all/order-fields on behalf of people who hold no Change All
    // grant at all — 37 logged 403s across 7 users in two days, every one of them
    // harmless (the fallback set covers it) and every one of them noise in
    // permission_denied_log. Deferring to change-all mode also means the request is
    // made by someone whose grant covers it.
    if (!active) return;
    if (resolved) return;
    let alive = true;
    fetchChangeAllOrderFields().then((fields) => {
      // A null answer means the server has no registry to offer. Leave the fallback in
      // place and stay `ready: false` — "we could not ask" is not "nothing is open".
      if (!fields) return;
      resolved = keysFrom(fields);
      if (alive) setKeys(resolved);
    });
    return () => { alive = false; };
  }, [active]);

  const effective = keys ?? CHANGE_ALL_ELIGIBLE_FIELDS;
  // Stable across renders while the set is unchanged — the banner memoizes its sweep
  // on this, and a fresh closure every render would recompute it on every keystroke.
  const isSweepable = useCallback(
    (viewKey: string) => effective.has(viewKey), [effective],
  );
  const isFannable = useCallback(
    // The exemption is checked FIRST and never consults the registry: bulk delete's
    // checkbox is armed by its own modal and must not depend on a flag that, by
    // design, is never set for that column. It is deliberately absent from
    // isSweepable above.
    (viewKey: string) => REGISTRY_EXEMPT_FIELDS.has(viewKey) || isSweepable(viewKey),
    [isSweepable],
  );
  return { ready: keys !== null, isFannable, isSweepable };
}

/**
 * Warm the module-scope answer for a user who is GOING to need it, without reading it.
 *
 * `useChangeAllFields` resolves once per page load and caches at module scope so a
 * later mount paints the right answer immediately. Deferring the fetch to change-all
 * mode (above) protects the denial log, but it also moved that first resolve to the
 * moment the first checkbox mounts — so on the first Change All of a page load the
 * checkboxes paint from the built-in fallback and correct themselves a tick later,
 * which is exactly the appears-then-vanishes flicker the module cache exists to
 * prevent, for any field an admin has closed.
 *
 * Calling this from a table that offers the Change All button restores the old
 * timing for the people who hold the grant, and still asks nothing on behalf of the
 * people who do not.
 */
export function usePrefetchChangeAllFields(active: boolean): void {
  useChangeAllFields(active);
}

/** Test aid — forget the module-scope answer so the next render refetches. */
export function _resetChangeAllFieldsCache(): void {
  resolved = null;
}
